import { useState } from "react";
import type { CMSTestimonialsSection, CMSTestimonialItem } from "@/mocks/cmsData";

interface Props {
  data: CMSTestimonialsSection;
  onChange: (data: CMSTestimonialsSection) => void;
}

export default function CMSTestimonialsEditor({ data, onChange }: Props) {
  const [lang, setLang] = useState<"ar" | "en">("ar");

  const update = (field: keyof CMSTestimonialsSection, value: unknown) => {
    onChange({ ...data, [field]: value });
  };

  const updateItem = (idx: number, field: keyof CMSTestimonialItem, value: string | number) => {
    const updated = data.items.map((item, i) =>
      i === idx ? { ...item, [field]: value } : item
    );
    update("items", updated);
  };

  const addItem = () => {
    update("items", [
      ...data.items,
      {
        name: "اسم المريض",
        name_en: "Patient Name",
        role: "مريض جديد",
        role_en: "New Patient",
        text: "تجربة رائعة مع الفريق الطبي المتميز.",
        text_en: "A wonderful experience with the outstanding medical team.",
        rating: 5,
        avatar: "https://readdy.ai/api/search-image?query=professional%20arab%20person%20portrait%20headshot%20neutral%20background%20confident%20smile%20clean%20modern%20style&width=80&height=80&seq=tnew&orientation=squarish",
      },
    ]);
  };

  const removeItem = (idx: number) => {
    update("items", data.items.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-6">
      {/* Language Switcher Bar */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
        <div>
          <h3 className="text-sm font-bold text-gray-800">لغة المحتوى (Content Language)</h3>
          <p className="text-xs text-gray-500">اختر اللغة لتعديل آراء المرضى ونصوص القسم</p>
        </div>
        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => setLang("ar")}
            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
              lang === "ar" ? "bg-white text-[#2E4E45] shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            العربية (AR)
          </button>
          <button
            type="button"
            onClick={() => setLang("en")}
            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
              lang === "en" ? "bg-white text-[#2E4E45] shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            English (EN)
          </button>
        </div>
      </div>

      {/* Header Texts */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-bold text-[#2E4E45] mb-5 flex items-center gap-2">
          <i className="ri-chat-quote-line text-base" />
          {lang === "ar" ? "نصوص القسم (العربية)" : "Section Texts (English)"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "الشارة (Badge)" : "Badge (EN)"}
            </label>
            <input
              type="text"
              value={lang === "ar" ? (data.badge || "") : (data.badge_en || "")}
              onChange={(e) => update(lang === "ar" ? "badge" : "badge_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "العنوان" : "Heading (EN)"}
            </label>
            <input
              type="text"
              value={lang === "ar" ? (data.heading || "") : (data.heading_en || "")}
              onChange={(e) => update(lang === "ar" ? "heading" : "heading_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              {lang === "ar" ? "الوصف" : "Description (EN)"}
            </label>
            <input
              type="text"
              value={lang === "ar" ? (data.description || "") : (data.description_en || "")}
              onChange={(e) => update(lang === "ar" ? "description" : "description_en", e.target.value)}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-bold text-[#2E4E45] flex items-center gap-2">
            <i className="ri-user-voice-line text-base" />
            {lang === "ar" ? `آراء المرضى (${data.items.length})` : `Patient Reviews (${data.items.length})`}
          </h3>
          <button
            type="button"
            onClick={addItem}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2E4E45]/8 text-[#2E4E45] text-xs font-semibold rounded-lg hover:bg-[#2E4E45]/15 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            {lang === "ar" ? "إضافة رأي" : "Add Review"}
          </button>
        </div>
        <div className="space-y-5">
          {data.items.map((item, idx) => (
            <div key={idx} className="border border-gray-100 rounded-xl p-5 bg-gray-50/50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-[#C8A96E]/30"
                  />
                  <div>
                    <p className="text-sm font-bold text-gray-800">
                      {lang === "ar" ? item.name : (item.name_en || item.name)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {lang === "ar" ? item.role : (item.role_en || item.role)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 hover:text-red-500 text-gray-400 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    {lang === "ar" ? "الاسم" : "Name (EN)"}
                  </label>
                  <input
                    type="text"
                    value={lang === "ar" ? (item.name || "") : (item.name_en || "")}
                    onChange={(e) => updateItem(idx, lang === "ar" ? "name" : "name_en", e.target.value)}
                    dir={lang === "ar" ? "rtl" : "ltr"}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    {lang === "ar" ? "الدور / الصفة" : "Role / Title (EN)"}
                  </label>
                  <input
                    type="text"
                    value={lang === "ar" ? (item.role || "") : (item.role_en || "")}
                    onChange={(e) => updateItem(idx, lang === "ar" ? "role" : "role_en", e.target.value)}
                    dir={lang === "ar" ? "rtl" : "ltr"}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1">
                    {lang === "ar" ? "نص الرأي" : "Testimonial Text (EN)"}
                  </label>
                  <textarea
                    value={lang === "ar" ? (item.text || "") : (item.text_en || "")}
                    onChange={(e) => updateItem(idx, lang === "ar" ? "text" : "text_en", e.target.value)}
                    dir={lang === "ar" ? "rtl" : "ltr"}
                    rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">التقييم (1-5)</label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => updateItem(idx, "rating", star)}
                        className={`w-7 h-7 flex items-center justify-center rounded cursor-pointer transition-colors ${
                          star <= item.rating ? "text-[#C8A96E]" : "text-gray-300"
                        }`}
                      >
                        <i className="ri-star-fill text-lg" />
                      </button>
                    ))}
                    <span className="text-xs text-gray-400 mr-1">{item.rating}/5</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">رابط الصورة</label>
                  <input
                    type="text"
                    value={item.avatar}
                    onChange={(e) => updateItem(idx, "avatar", e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2E4E45] transition-colors"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
